<template>
  <div id="stripeapi">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="stripeapi-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <CodeIcon :color="iconColor" class="stripeapi-icon" /> STRIPE API
            </h1>
            <div class="mt-8">
              <h2>List A Customer's Transaction</h2>
              <v-text-field
                v-model="customerEmail"
                label="Customer Email"
                variant="underlined"
                color="#A0C548"
                hint="Enter the email of the customer to fetch their info."
                persistent-hint
              />
              <v-btn
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold app-btn"
                :disabled="!customerEmail || loadingCustomerInfo"
                @click="getCustomerInfo"
              >
                <template v-if="!loadingCustomerInfo">
                  Get Customer Info
                </template>
                <template v-else>
                  {{ loadingMessage }}
                  <!-- Use dynamic loading message -->
                </template>
              </v-btn>

              <v-progress-linear
                v-if="loadingCustomerInfo"
                color="#592892"
                indeterminate
                class="mt-3"
              ></v-progress-linear>
            </div>
          </div>
          <JsonViewer
            v-if="customerInfo"
            :value="customerInfo"
            :expanded="true"
            :expand-depth="Infinity"
            :copyable="true"
            theme="dark"
            class="rounded"
          />
        </div>

        <div class="stripeapi-container">
          <div class="route-container mb-4 section-heading">
            <h2>Create Coupon</h2>
            <v-text-field
              v-model="couponName"
              label="Name"
              variant="underlined"
              color="#A0C548"
              hint="This will appear on customers' receipts and invoices."
              persistent-hint
            />
            <v-text-field
              v-model="couponAmountOff"
              label="Amount Off"
              variant="underlined"
              color="#A0C548"
              hint="Enter the fixed amount discount for this coupon."
              persistent-hint
              type="number"
            />
            <v-select
              v-model="couponDuration"
              :items="couponDurationLabels"
              item-title="text"
              item-value="value"
              label="Duration"
              variant="underlined"
              color="#A0C548"
              hint="Select the duration for which this coupon will be valid."
              persistent-hint
            />
            <v-text-field
              v-model="couponMaxRedemptions"
              label="Max Redemptions"
              variant="underlined"
              color="#A0C548"
              hint="Enter the maximum number of times this coupon can be redeemed. Leave blank for unlimited redemptions."
              persistent-hint
              type="number"
            />
            <v-autocomplete
              v-model="selectedProducts"
              :items="productOptions"
              item-title="name"
              item-value="id"
              label="Apply to Specific Products"
              variant="underlined"
              color="#A0C548"
              multiple
              chips
              :loading="loadingProducts"
              @focus="fetchProducts"
              hint="Select products to which this coupon will apply. Leave empty to apply the coupon to all products."
              persistent-hint
            />
            <v-text-field
              v-model="couponCode"
              label="Coupon Code"
              variant="underlined"
              color="#A0C548"
              hint="Optional: Enter a code for customers to use this coupon. If left blank, a code will be auto-generated."
              persistent-hint
            />
            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold app-btn"
              :disabled="!couponName || !couponAmountOff || !couponDuration"
              @click="createCoupon"
              >Create Coupon</v-btn
            >
            <v-progress-linear
              v-if="creatingCoupon"
              color="#592892"
              indeterminate
              class="mt-3"
            ></v-progress-linear>
          </div>
          <JsonViewer
            v-if="couponInfo"
            :value="couponInfo"
            :expanded="true"
            :expand-depth="Infinity"
            :copyable="true"
            theme="dark"
            class="rounded"
          />
        </div>
        <!-- Display Coupons -->
        <div class="coupon-table mt-14">
          <h2>List of Coupons</h2>
          <v-data-table
            :headers="couponHeaders"
            :items="coupons"
            :loading="isloading"
            loading-text="Loading Coupons... Please wait"
            no-data-text="No Coupons"
            :items-per-page="20"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, -1],
            }"
            item-key="id"
            class="elevation-1 users-table mt-4"
          >
            <template #progress>
              <v-progress-linear
                color="#592892"
                indeterminate
              ></v-progress-linear>
            </template>

            <template #headers>
              <tr>
                <th class="text-left">ID</th>
                <th class="text-left">Promotion Code</th>
                <th class="text-left">Percent Off</th>
                <th class="text-left">Amount Off</th>
                <th class="text-left">Max Redemptions</th>
                <th class="text-left">Times Redeemed</th>
                <th class="text-right">Actions</th>
              </tr>
            </template>

            <!-- Show Promotion Code -->
            <template #item.promo_code="{ item }">
              <span v-if="item.promo_code">{{ item.promo_code }}</span>
            </template>

            <!-- Show percent_off only if it exists -->
            <template #item.percent_off="{ item }">
              <span v-if="item.percent_off">{{ item.percent_off }}%</span>
            </template>

            <!-- Show amount_off only if it exists -->
            <template #item.amount_off="{ item }">
              <span v-if="item.amount_off"
                >${{ (item.amount_off / 100).toFixed(2) }}</span
              >
            </template>

            <template #item.times_redeemed="{ item }">
              <span>{{ formatTimesRedeemed(item) }}</span>
            </template>

            <!-- Actions template (e.g., delete icon) -->
            <template #item.actions="{ item }">
              <div class="action-icons">
                <v-icon
                  v-if="!isHelpDesk"
                  @click.native="deleteCoupon(item)"
                  >delete_icon</v-icon
                >
              </div>
            </template>
          </v-data-table>
        </div>
      </v-flex>
      <v-snackbar
        v-model="snackbar"
        color="#592892"
        :timeout="timeout"
        location="top"
      >
        <span v-html="snackbarMsg"></span>
        <template #actions="{ attrs }">
          <v-btn
            variant="text"
            v-bind="attrs"
            @click="snackbar = false"
            class="font-weight-bold"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
  import axios from 'axios'
  import { functionUrl } from '@/helpers/firebaseEnv'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapGetters } from 'vuex'
  import { confirmAction } from '@/components/utilities/SwalService'
  import CodeIcon from '@/assets/icons/CodeIcon.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'StripeKey',
    data() {
      return {
        snackbar: false,
        snackbarMsg: '',
        timeout: 6000,
        loadingCustomerInfo: false,
        loadingMessage: 'Loading...',
        loadingTimeouts: [],
        customerEmail: '',
        customerInfo: null,
        couponName: '',
        // couponPercentOff: '',
        couponAmountOff: '',
        couponDuration: null,
        // couponId: '', // Commented out
        couponMaxRedemptions: '',
        couponProductIds: '',
        couponCode: '',
        couponInfo: null,
        productOptions: [],
        selectedProducts: [],
        loadingProducts: false,
        productsLoaded: false, // Add a flag to track if products are loaded
        coupons: [],
        couponHeaders: [
          { text: 'ID', value: 'id' },
          // { text: 'Name', value: 'name' },
          { text: 'Promotion Code', value: 'promo_code' },
          { text: 'Percent Off', value: 'percent_off' },
          { text: 'Amount Off', value: 'amount_off' },
          { text: 'Max Redemptions', value: 'max_redemptions' },
          { text: 'Times Redeemed', value: 'times_redeemed' },
          { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
        ],
        isloading: false,
        creatingCoupon: false,
        couponDurationLabels: [
          { text: 'Forever', value: 'forever' },
          { text: 'Once', value: 'once' },
          { text: 'Repeating', value: 'repeating' },
        ],
      }
    },
    components: {
      HowToUseThisPage,
      CodeIcon,
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      isHelpDesk() {
        const roles = this.$store.getters['UserData/getRoles'] || []
        return roles.includes('helpDesk')
      },
    },
    async created() {
      this.isloading = true
      await this.fetchCoupons() // Fetch coupons when component is created
    },
    methods: {
      formatTimesRedeemed(coupon) {
        const used =
          typeof coupon?.times_redeemed === 'number' ? coupon.times_redeemed : 0
        const max = coupon?.max_redemptions
        if (max == null) {
          return `${used} / ∞`
        }
        return `${used} / ${max}`
      },
      async fetchCoupons() {
        try {
          // Fetch combined coupons and promotion codes from the backend
          const response = await axios.get(
            functionUrl('listCoupons')
          )
          this.coupons = response.data // response.data should now include promo_code in each coupon object
        } catch (error) {
          console.error('Error fetching coupons:', error)
        } finally {
          this.isloading = false // Stop the progress bar
        }
      },
      async deleteCoupon(coupon) {
        if (this.isHelpDesk) {
          const key = 'api.deleteNotAllowed'
          const message =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete coupons.'
          this.snackbar = true
          this.snackbarMsg = message
          return
        }

        if (!coupon || !coupon.id) {
          console.error('Error: Invalid coupon object or missing coupon ID')
          return
        }

        const confirmed = await confirmAction({
          title: 'You are about to delete:',
          html: `"${coupon.id}"<br>Type DELETE below to confirm.`,
          input: 'text',
          inputPlaceholder: 'Type DELETE',
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'DELETE')) {
                resolve()
              } else {
                resolve('You need to type DELETE to confirm')
              }
            })
          },
          confirmButtonText: 'Yes, Delete it!',
          cancelButtonText: 'No, Keep it!',
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        this.isloading = true

        try {
          const response = await axios.delete(
            functionUrl('deleteCoupon'),
            { data: { couponId: coupon.id } }
          )

          if (response.status === 200) {
            this.snackbarMsg = `Coupon ${coupon.id} deleted successfully`
            this.snackbar = true
            await this.fetchCoupons()
          } else {
            console.error(
              `Error deleting coupon ${coupon.id}:`,
              response.data.error
            )
            this.snackbarMsg = `Failed to delete coupon ${coupon.id}`
            this.snackbar = true
          }
        } catch (error) {
          console.error(`Error deleting coupon ${coupon.id}:`, error)
          this.snackbarMsg = `Failed to delete coupon ${coupon.id}`
          this.snackbar = true
        } finally {
          this.isloading = false
        }
      },
      async getCustomerInfo() {
        this.loadingCustomerInfo = true
        this.loadingMessage = 'Loading...'

        // Set timeouts to update the loading message at different intervals
        this.loadingTimeouts.push(
          setTimeout(() => {
            this.loadingMessage = 'Still loading...' // After 2 seconds
          }, 4000)
        )

        this.loadingTimeouts.push(
          setTimeout(() => {
            this.loadingMessage = 'Bare with us, almost there...' // After 4 seconds
          }, 8000)
        )

        try {
          const response = await axios.post(
            functionUrl('getCustomerInfo'),
            {
              email: this.customerEmail,
            }
          )
          if (response.data && response.data.length > 0) {
            this.customerInfo = JSON.parse(
              JSON.stringify({
                ...response.data[0],
                created: response.data[0].created
                  ? new Date(response.data[0].created * 1000).toISOString()
                  : response.data[0].created,
              })
            )
          } else {
            this.customerInfo = null
          }
          this.snackbarMsg = `Customer Info Retrieved`
          this.snackbar = true
        } catch (error) {
          this.snackbarMsg = `Error: ${
            error.response.data.message || error.message
          }`
          this.snackbar = true
        } finally {
          this.clearLoadingTimeouts()
          this.loadingCustomerInfo = false
          this.loadingMessage = 'Loading...'
        }
      },
      clearLoadingTimeouts() {
        // Clear any active timeouts
        this.loadingTimeouts.forEach((timeout) => clearTimeout(timeout))
        this.loadingTimeouts = []
      },
      async createCoupon() {
        if (!this.couponName) {
          this.snackbarMsg = `Error: Coupon name is required`
          this.snackbar = true
          return
        }

        // Sanitize the coupon name for the ID
        const sanitizedCouponName = this.couponName
          .replace(/\s+/g, '_')
          .toUpperCase() // Replace spaces with underscores, make uppercase

        this.creatingCoupon = true // Start the progress bar

        try {
          const validProductIds = this.selectedProducts.filter(
            (productId) =>
              typeof productId === 'string' && productId.trim() !== ''
          )

          const couponData = {
            amount_off: this.couponAmountOff, // Make sure this is populated
            duration: this.couponDuration,
            id: sanitizedCouponName, // Use couponName as id
            name: this.couponName,
            max_redemptions: this.couponMaxRedemptions || undefined,
            product_ids: validProductIds.length > 0 ? validProductIds : [],
          }

          // console.log("Coupon Data to be sent:", couponData);  // Log the entire data

          const couponResponse = await axios.post(
            functionUrl('createCoupon'),
            couponData
          )

          if (couponResponse.data) {
            this.couponInfo = couponResponse.data

            // Auto-generate a promotion code if couponCode is empty
            let promotionCode =
              this.couponCode ||
              `JOURNEY${Math.random()
                .toString(36)
                .substring(2, 8)
                .toUpperCase()}`

            const sanitizedCouponCode = promotionCode
              .replace(/\s+/g, '_')
              .toUpperCase() // Sanitize coupon code

            const promotionCodeData = {
              coupon: sanitizedCouponName,
              code: sanitizedCouponCode, // Use sanitized or auto-generated code
              max_redemptions: this.couponMaxRedemptions || undefined,
            }

            const promotionCodeResponse = await axios.post(
              functionUrl('createPromotionCode'),
              promotionCodeData
            )

            if (promotionCodeResponse.data) {
              this.promotionCodeInfo = promotionCodeResponse.data
              this.snackbarMsg = `Coupon and Promotion Code Created Successfully`
            } else {
              this.promotionCodeInfo = null
              this.snackbarMsg = `Coupon Created Successfully, but Failed to Create Promotion Code`
            }
          } else {
            this.couponInfo = null
            this.snackbarMsg = `Failed to Create Coupon`
          }

          this.snackbar = true
          await this.fetchCoupons()
        } catch (error) {
          console.error('Error:', error) // Log the error
          this.snackbarMsg = `Error: ${
            error.response ? error.response.data.message : error.message
          }`
          this.snackbar = true
        } finally {
          this.creatingCoupon = false // Stop the progress bar
        }
      },
      async fetchProducts() {
        if (this.productsLoaded) {
          return // Prevent re-fetching if products are already loaded
        }

        this.loadingProducts = true
        try {
          const response = await axios.get(
            functionUrl('getProducts')
          )
          this.productOptions = response.data.map((product) => ({
            id: product.id,
            name: product.name,
          }))
          this.productsLoaded = true // Mark products as loaded
        } catch (error) {
          console.error('Error fetching products:', error)
        } finally {
          this.loadingProducts = false
        }
      },
    },
  }
</script>

<style scoped>
  /* .stripeapi-container {
    margin-top: 50px;
    margin-bottom: 25%;
} */
  .stripeapi-container .stripeapi-icon {
    margin-right: 10px;
  }
  .coupon-table {
    margin-bottom: 25%;
  }
  .dark-theme {
    background-color: #333;
    color: #fff;
    padding: 20px;
    border-radius: 5px;
  }
  .dark-theme .__json-pretty__value-key,
  .dark-theme .__json-pretty__value-string,
  .dark-theme .__json-pretty__value-number,
  .dark-theme .__json-pretty__value-boolean,
  .dark-theme .__json-pretty__value-null {
    color: #fff;
  }
  .dark-theme :deep(.vjs-tree-node:hover) {
    background-color: #5a5a5a;
    /* color: #121212; */
  }
</style>
